import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Alert, Col, Container, Input, Row, Spinner } from "reactstrap";
import AdminTablePagination from "../../../Components/Common/AdminTablePagination";
import {
  AdminUserListItem,
  AdminUserSortProperty,
  AdminUserStats,
  getAdminUserStats,
  getAdminUsers,
} from "../../../helpers/api/admin/user";
import { useAuthStore } from "../../../store/useAuthStore";

type SortOrder = "ASC" | "DESC";

const defaultStats: AdminUserStats = {
  totalUsers: 0,
  verifiedUsers: 0,
  unverifiedUsers: 0,
  registeredThisMonth: 0,
  connectedThisMonth: 0,
};

const formatNumber = (value?: number | null) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  return value.toLocaleString("en-US");
};

export const shortenWallet = (value?: string | null) => {
  if (!value) return "-";
  if (value.length <= 12) return value;
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
};

export const isVerified = (value?: number | null) => value === 1;

const getDisplayName = (user: AdminUserListItem) => user.username || shortenWallet(user.walletAddress);

const getInitial = (user: AdminUserListItem) => {
  const label = getDisplayName(user);
  return label && label !== "-" ? label.charAt(0).toUpperCase() : "?";
};

const getNetClassName = (value: number) => (
  value < 0 ? "admin-users-value--negative" : "admin-users-value--positive"
);

const getSortLabel = (property: AdminUserSortProperty, sortBy: AdminUserSortProperty, sortOrder: SortOrder) => {
  if (property !== sortBy) return "Sort";
  return sortOrder === "ASC" ? "Sorted ascending" : "Sorted descending";
};

const User = () => {
  document.title = "Admin Colosseum | User management";

  const { isAdmin } = useAuthStore();
  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [stats, setStats] = useState<AdminUserStats>(defaultStats);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(15);
  const [sortBy, setSortBy] = useState<AdminUserSortProperty>("username");
  const [sortOrder, setSortOrder] = useState<SortOrder>("ASC");
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const totalPages = useMemo(() => Math.max(1, Math.ceil((totalUsers || 0) / pageSize)), [totalUsers, pageSize]);
  const displayedTotal = users.length > 0 ? totalUsers : 0;
  const fromRow = displayedTotal > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const toRow = displayedTotal > 0 ? Math.min(fromRow + users.length - 1, displayedTotal) : 0;
  const verifiedRatio = stats.totalUsers > 0 ? Math.round((stats.verifiedUsers / stats.totalUsers) * 100) : 0;
  const connectedRatio = stats.totalUsers > 0 ? Math.round((stats.connectedThisMonth / stats.totalUsers) * 100) : 0;
  const newRatio = stats.totalUsers > 0 ? Math.round((stats.registeredThisMonth / stats.totalUsers) * 100) : 0;

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const value = search.trim();
      setDebouncedSearch(value.length >= 3 ? value : "");
      setCurrentPage(1);
    }, 300);

    return () => window.clearTimeout(handle);
  }, [search]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextStats, nextUsers] = await Promise.all([
        getAdminUserStats(),
        getAdminUsers({
          page: currentPage,
          limit: pageSize,
          sort: sortBy,
          order: sortOrder,
          query: debouncedSearch || undefined,
        }),
      ]);
      setStats(nextStats);
      setUsers(nextUsers.items);
      setTotalUsers(nextUsers.meta.totalItems);
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "Failed to load users");
      setUsers([]);
      setStats(defaultStats);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sortBy, sortOrder, debouncedSearch]);

  const handleSort = (property: AdminUserSortProperty) => {
    setCurrentPage(1);
    if (property === sortBy) {
      setSortOrder((current) => (current === "ASC" ? "DESC" : "ASC"));
      return;
    }
    setSortBy(property);
    setSortOrder("ASC");
  };

  if (!isAdmin()) {
    return <Navigate to={{ pathname: "/dashboard" }} />;
  }

  return (
    <div className="page-content admin-users-page" data-cy="admin-users-page">
      <Container fluid>
        <div className="admin-users-heading">
          <h4 className="mb-1">
            <span className="gradient-text-brand">User management</span>
          </h4>
          <p className="text-muted mb-0">Search, audit and manage every champion in the arena.</p>
        </div>

        {error && <Alert color="danger" role="alert">{error}</Alert>}

        <Row className="g-3 admin-users-summary">
          <Col md={4}>
            <div className="admin-users-stat">
              <span>Total users</span>
              <strong>{formatNumber(stats.totalUsers)}</strong>
              <small>
                <b className="admin-users-value--positive">{formatNumber(stats.verifiedUsers)} verified</b>
                <b className="admin-users-value--warning">{formatNumber(stats.unverifiedUsers)} not verified</b>
              </small>
            </div>
          </Col>
          <Col md={4}>
            <div className="admin-users-stat">
              <span>New accounts · this month</span>
              <strong>{formatNumber(stats.registeredThisMonth)}</strong>
              <small><b className="admin-users-value--positive">{newRatio}% of all accounts</b></small>
            </div>
          </Col>
          <Col md={4}>
            <div className="admin-users-stat">
              <span>Active users · this month</span>
              <strong>{formatNumber(stats.connectedThisMonth)}</strong>
              <small><b className="admin-users-value--positive">{connectedRatio}% of total logged in</b></small>
            </div>
          </Col>
        </Row>

        <div className="admin-users-panel">
          <div className="admin-users-toolbar">
            <label className="visually-hidden" htmlFor="admin-user-search">Quick search</label>
            <Input
              id="admin-user-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Quick search - wallet, email or username..."
              data-cy="admin-user-search"
            />
            <span className="admin-users-toolbar-meta">{verifiedRatio}% verified</span>
          </div>

          <div className="admin-users-table-wrap">
            <table className="admin-users-table">
              <thead>
                <tr>
                  <th>
                    <button type="button" onClick={() => handleSort("username")} aria-label={getSortLabel("username", sortBy, sortOrder)}>
                      User <i className="ri-arrow-up-down-line" aria-hidden="true" />
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => handleSort("email")} aria-label={getSortLabel("email", sortBy, sortOrder)}>
                      Email <i className="ri-arrow-up-down-line" aria-hidden="true" />
                    </button>
                  </th>
                  <th>Verified</th>
                  <th>
                    <button type="button" onClick={() => handleSort("totalBets")} aria-label={getSortLabel("totalBets", sortBy, sortOrder)}>
                      Bets (W / L) <i className="ri-arrow-up-down-line" aria-hidden="true" />
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => handleSort("pointsBalance")} aria-label={getSortLabel("pointsBalance", sortBy, sortOrder)}>
                      Balance <i className="ri-arrow-up-down-line" aria-hidden="true" />
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => handleSort("netResult")} aria-label={getSortLabel("netResult", sortBy, sortOrder)}>
                      Net result <i className="ri-arrow-up-down-line" aria-hidden="true" />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center">
                      <Spinner size="sm" className="me-2" /> Loading users...
                    </td>
                  </tr>
                ) : users.length > 0 ? (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="admin-users-identity">
                          <span className="admin-users-avatar">{getInitial(user)}</span>
                          <span>
                            <Link
                              to={`/admin/users/${user.id}`}
                              state={{ listUser: user }}
                              className="admin-users-name"
                              data-cy="admin-user-link"
                            >
                              {getDisplayName(user)}
                            </Link>
                            <small>{shortenWallet(user.walletAddress)}</small>
                          </span>
                        </div>
                      </td>
                      <td>{user.email || "-"}</td>
                      <td>
                        <span className={isVerified(user.verify) ? "admin-users-badge admin-users-badge--yes" : "admin-users-badge admin-users-badge--no"}>
                          {isVerified(user.verify) ? "Yes" : "No"}
                        </span>
                      </td>
                      <td>
                        <strong>{formatNumber(user.totalBets)}</strong>
                        <small className="admin-users-bets">
                          <b className="admin-users-value--positive">{formatNumber(user.wonBets)}W</b>
                          <b className="admin-users-value--negative">{formatNumber(user.lostBets)}L</b>
                        </small>
                      </td>
                      <td><strong>{formatNumber(user.pointsBalance)}</strong> <small>PTS</small></td>
                      <td><strong className={getNetClassName(user.netResult)}>{formatNumber(user.netResult)}</strong></td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center text-muted">No users found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="admin-users-footer">
            <div className="admin-users-page-size">
              <label htmlFor="admin-users-page-size">Rows</label>
              <Input
                id="admin-users-page-size"
                type="select"
                value={pageSize}
                onChange={(event) => {
                  setCurrentPage(1);
                  setPageSize(parseInt(event.target.value, 10));
                }}
                data-cy="admin-users-page-size"
              >
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Input>
              <span>Showing {fromRow}-{toRow} of {displayedTotal} users</span>
            </div>
            <AdminTablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              disabled={loading}
              onPageChange={setCurrentPage}
              nextDataCy="admin-users-next-page"
            />
          </div>
        </div>
      </Container>
    </div>
  );
};

export default User;
